import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  useIonToast
} from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, orderBy, getDocs, updateDoc, doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { Notification } from '../types/notification';
import { Box, Typography, IconButton, List, ListItem, ListItemText, Divider } from '@mui/material';
import { Close } from '@mui/icons-material';
import { format } from 'date-fns';

// Interface for profile visitors
interface ProfileVisitor {
  userId: string;
  userName: string;
  timestamp: Date;
}

interface NotificationsProps {
  onClose?: () => void;
}

const NotificationsPage: React.FC<NotificationsProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [presentToast] = useIonToast();

  const fetchNotifications = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Fetch regular notifications
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef,
        where('toUserId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const notificationsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate()
      })) as Notification[];

      // Filter out notifications where the user is the sender
      const filteredNotifications = notificationsData.filter(
        notification => notification.fromUserId !== user.uid
      );

      // Fetch profile visits from profileVisits collection
      const profileVisitsRef = collection(db, 'profileVisits');
      const profileVisitsQuery = query(
        profileVisitsRef,
        where('toUserId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
      
      const profileVisitsSnapshot = await getDocs(profileVisitsQuery);
      const profileVisits = profileVisitsSnapshot.docs.map(doc => ({
        id: doc.id,
        type: 'profile_visit',
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate()
      })) as Notification[];
      
      // Combine regular notifications with profile visits
      const allNotifications = [...filteredNotifications, ...profileVisits];
      
      // Sort by date, newest first
      allNotifications.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setNotifications(allNotifications);

      // Mark all regular notifications as read
      const batch = [];
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.read && data.fromUserId !== user.uid) {
          batch.push(updateDoc(doc.ref, { read: true }));
        }
      });
      
      // Mark all profile visits as read
      profileVisitsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!data.read) {
          batch.push(updateDoc(doc.ref, { read: true }));
        }
      });
      
      await Promise.all(batch);

    } catch (error) {
      console.error('Error fetching notifications:', error);
      presentToast({
        message: t('notifications.fetch_error'),
        duration: 2000,
        position: 'bottom',
        color: 'danger'
      });
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    try {
      await fetchNotifications();
      event.detail.complete();
    } catch (error) {
      event.detail.complete();
      presentToast({
        message: t('notifications.refresh_error'),
        duration: 2000,
        position: 'bottom',
        color: 'danger'
      });
    }
  };

  const findConversationWithUser = async (otherUserId: string) => {
    try {
      const user = auth.currentUser;
      if (!user) return;

      // Check if conversation already exists
      const conversationsRef = collection(db, 'conversations');
      const q = query(
        conversationsRef,
        where('participants', 'array-contains', user.uid)
      );
      const querySnapshot = await getDocs(q);
      
      const existingConversation = querySnapshot.docs.find(doc => {
        const data = doc.data();
        return data.participants.includes(otherUserId);
      });

      let conversationId;
      
      if (existingConversation) {
        conversationId = existingConversation.id;
      } else {
        // If no conversation exists, redirect to the user's profile instead
        history.push(`/profile/${otherUserId}`);
        return;
      }

      // Navigate to messages page with the conversation selected
      history.push(`/messages?conversation=${conversationId}`);
    } catch (error) {
      console.error('Error finding conversation:', error);
      presentToast({
        message: t('notifications.error_finding_conversation'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  const getNotificationText = (notification: Notification) => {
    switch (notification.type) {
      case 'like':
        return t('notifications.liked_post', { user: notification.fromUserName });
      case 'message':
        return t('notifications.new_message', { user: notification.fromUserName });
      case 'profile_visit':
        return t('notifications.profile_visit', { user: notification.fromUserName });
      default:
        return '';
    }
  };

  const content = (
    <>
      {onClose ? (
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>{t('notifications.title')}</Typography>
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('notifications.title')}</IonTitle>
          </IonToolbar>
        </IonHeader>
      )}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <IonContent>
          {/* Temporarily disabled IonRefresher due to initialization issues */}
          {/* <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
            <IonRefresherContent />
          </IonRefresher> */}

          {notifications.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              {t('notifications.no_notifications')}
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {notifications.map(notification => (
                <ListItem
                  key={notification.id}
                  divider
                  onClick={() => {
                    if (onClose) {
                      onClose();
                    }
                    if (notification.type === 'profile_visit' || notification.type === 'like') {
                      history.push(`/profile/${notification.fromUserId}`);
                    } else if (notification.type === 'message') {
                      findConversationWithUser(notification.fromUserId);
                    }
                  }}
                  sx={{
                    py: 1,
                    backgroundColor: notification.read ? 'transparent' : 'action.hover',
                    cursor: ['profile_visit', 'like', 'message'].includes(notification.type) ? 'pointer' : 'default'
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: notification.read ? 'normal' : 500,
                          fontSize: '0.9rem',
                          lineHeight: 1.3
                        }}
                      >
                        {getNotificationText(notification)}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontSize: '0.75rem',
                          mt: 0.5
                        }}
                      >
                        {format(notification.createdAt, 'PPp')}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </IonContent>
      </Box>
    </>
  );

  // If rendered in dialog (onClose present), return content directly
  if (onClose) {
    return content;
  }

  // If rendered as a page, wrap in IonPage
  return (
    <IonPage>
      {content}
    </IonPage>
  );
};

export default NotificationsPage;
